/**
 * 📥 IN-APP NOTIFICATION BANNER
 * 
 * Slides down from top when notification arrives while user is in-app
 * but NOT on the chat screen. Provides gentle alert without interrupting
 * current activity.
 * 
 * Behavior:
 * - Slides down from top (animated)
 * - Shows for 5 seconds OR until tapped
 * - Tap → Navigate to chat
 * - Auto-dismiss → Notification stays in chat as pill
 * 
 * Mission 11: Unified Notifications - In-App Alert System
 * Date: January 31, 2026
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackNudge, type NudgeSubtype } from '../../services/analyticsEvents';

interface NotificationBannerProps {
  title: string;
  message: string;
  icon?: string | React.ReactNode;
  onDismiss: () => void;
  conversationId?: string;
  /** Analytics classification — defaults to 'generic' if not provided. */
  nudgeType?: NudgeSubtype;
}

export const InAppNotificationBanner: React.FC<NotificationBannerProps> = ({
  title,
  message,
  icon = '💬',
  onDismiss,
  conversationId,
  nudgeType,
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // Platform Analytics: banner shown to user.
  useEffect(() => {
    void trackNudge('open', nudgeType ?? 'generic', {
      conversation_id: conversationId || null,
    });
    // Only fire once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      // Auto-dismiss — user didn't engage.
      void trackNudge('dismiss', nudgeType ?? 'generic', { reason: 'timeout' });
      handleDismiss();
    }, 5000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss();
    }, 300); // Match animation duration
  };

  const handleTap = () => {
    // Platform Analytics: user tapped through.
    void trackNudge('tap', nudgeType ?? 'generic', {
      conversation_id: conversationId || null,
    });
    handleDismiss();

    // Navigate to chat with conversation context
    navigate('/chat', {
      state: {
        from: 'notification-banner',
        conversationId: conversationId,
        timestamp: Date.now()
      }
    });
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] pointer-events-none ${
        isExiting ? 'animate-slideOutUp' : 'animate-slideInDown'
      }`}
      role="alert"
      aria-live="polite"
    >
      {/* Safe area padding for mobile notch */}
      <div style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Banner content - enable pointer events here */}
        <div className="px-4 pt-3 pb-2 pointer-events-auto">
          <div
            onClick={handleTap}
            className="bg-white rounded-xl shadow-lg border-l-4 border-emerald-500 p-4 cursor-pointer hover:shadow-xl transition-shadow"
            role="button"
            aria-label="Tap to view notification in chat"
          >
            <div className="flex items-start gap-3">
              {/* Icon — supports both emoji strings and React component icons */}
              <div className="flex-shrink-0 mt-0.5" style={typeof icon === 'string' ? { fontSize: '1.5rem', lineHeight: 1 } : { width: 28, height: 28 }}>
                {icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Title */}
                <div className="font-semibold text-gray-900 text-sm mb-1">
                  {title}
                </div>

                {/* Message - truncate if too long */}
                <div className="text-gray-600 text-xs leading-relaxed line-clamp-2">
                  {message}
                </div>
              </div>

              {/* Dismiss button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Don't trigger navigation
                  handleDismiss();
                }}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Dismiss notification"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 🎯 IN-APP NOTIFICATION BANNER MANAGER
 * 
 * Manages queue of banners (only shows one at a time)
 * Integrates with notification polling system
 */

interface BannerNotification {
  id: string;
  title: string;
  message: string;
  icon?: string | React.ReactNode;
  conversationId?: string;
  timestamp: number;
  /** Platform analytics classification for the Relationship tab. */
  nudgeType?: NudgeSubtype;
}

export const InAppNotificationBannerManager: React.FC = () => {
  const [bannerQueue, setBannerQueue] = useState<BannerNotification[]>([]);
  const [currentBanner, setCurrentBanner] = useState<BannerNotification | null>(null);
  const location = useLocation();
  // De-duplicate: track notification IDs we've already shown or queued
  const shownIdsRef = React.useRef(new Set<string>());

  // Subscribe to notification events
  useEffect(() => {
    const handleNotificationEvent = (event: CustomEvent<BannerNotification>) => {
      const notification = event.detail;

      // Only show banner if NOT on chat screen
      if (location.pathname === '/chat') {
        return;
      }

      // De-duplicate: skip if we've already shown/queued this notification
      if (notification.id && shownIdsRef.current.has(notification.id)) {
        return;
      }
      if (notification.id) {
        shownIdsRef.current.add(notification.id);
        // L6 FIX (Mission 35B): Replaced O(n) Array.from+slice+loop with
        // simple Set reconstruction — clear and rebuild from the last 100 entries.
        if (shownIdsRef.current.size > 200) {
          const keep = [...shownIdsRef.current].slice(-100);
          shownIdsRef.current = new Set(keep);
        }
      }

      setBannerQueue(prev => [...prev, notification]);
    };

    // Listen for custom notification events
    window.addEventListener('coach-cedar-notification' as any, handleNotificationEvent);

    return () => {
      window.removeEventListener('coach-cedar-notification' as any, handleNotificationEvent);
    };
  }, [location.pathname]);

  // Display next banner when current one is dismissed
  useEffect(() => {
    if (!currentBanner && bannerQueue.length > 0) {
      const [nextBanner, ...rest] = bannerQueue;
      setCurrentBanner(nextBanner);
      setBannerQueue(rest);
    }
  }, [currentBanner, bannerQueue]);

  const handleDismiss = () => {
    console.log('🔕 Banner dismissed');
    setCurrentBanner(null);
  };

  if (!currentBanner) return null;

  return (
    <InAppNotificationBanner
      title={currentBanner.title}
      message={currentBanner.message}
      icon={currentBanner.icon}
      conversationId={currentBanner.conversationId}
      nudgeType={currentBanner.nudgeType}
      onDismiss={handleDismiss}
    />
  );
};

export default InAppNotificationBannerManager;
